import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, UserCheck, UserX, ClipboardList, Trash2, RefreshCw, Lock, Pencil, Plus, Globe, Eye, Search, Download, ChevronDown, ChevronUp, Moon, Sun, Bell, Calendar, QrCode, TrendingUp, Clock, PartyPopper, Target, Home, Printer, KeyRound, Volume2, VolumeX, PieChart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 20;
const GUEST_TARGET = 130;
const MENU_OPTIONS = [{ value: "teletina", label: "Teletina" }, { value: "piletina", label: "Piletina" }, { value: "djeciji", label: "Dječiji meni" }];
const getMenuLabel = (val) => !val ? "-" : MENU_OPTIONS.find(o => o.value === val)?.label || val;

// Count-up hook
function useCountUp(target, duration = 1200) {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    if (target === prev.current) return;
    const start = prev.current;
    const diff = target - start;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= duration) { setVal(target); prev.current = target; return; }
      const progress = elapsed / duration;
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(start + diff * eased));
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return val;
}

// 3D Countdown Cubes
function Countdown({ dark }) {
  const [d, setD] = useState(0); const [h, setH] = useState(0); const [m, setM] = useState(0);
  const [flip, setFlip] = useState(false);
  useEffect(() => {
    const calc = () => {
      const diff = new Date("2026-05-09T16:30:00") - new Date();
      if (diff <= 0) return;
      setD(Math.floor(diff / 86400000));
      setH(Math.floor((diff % 86400000) / 3600000));
      setM(Math.floor((diff % 3600000) / 60000));
      setFlip(f => !f);
    };
    calc(); const i = setInterval(calc, 60000); return () => clearInterval(i);
  }, []);
  const tp = dark ? "text-white" : "text-wedding-text";
  const cubeBg = dark ? "bg-[#2a2a2a]" : "bg-white";
  const ts = dark ? "text-gray-500" : "text-wedding-text-secondary/60";
  return (
    <div className="flex items-center gap-6 justify-center">
      {[{ v: d, l: "dana" }, { v: h, l: "sati" }, { v: m, l: "min" }].map((x, i) => (
        <div key={i} className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-1" style={{ perspective: "200px" }}>
            <div className={`absolute inset-0 ${cubeBg} rounded-lg shadow-lg border ${dark ? "border-[#333]" : "border-wedding-border/50"} flex items-center justify-center`} style={{
              transform: flip ? "rotateX(0deg)" : "rotateX(-10deg)",
              transition: "transform 0.6s cubic-bezier(0.4,0,0.2,1)",
              transformStyle: "preserve-3d",
              backfaceVisibility: "hidden"
            }}>
              <span className={`font-serif text-3xl ${tp}`}>{x.v}</span>
            </div>
          </div>
          <p className={`font-sans text-[10px] uppercase tracking-wider ${ts}`}>{x.l}</p>
        </div>
      ))}
    </div>
  );
}

// Confetti
function Confetti({ active }) {
  if (!active) return null;
  const colors = ["#C5A059", "#EADCAA", "#9E7E40", "#FFD700", "#FFF8E7"];
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {Array.from({ length: 50 }, (_, i) => (
        <div key={i} style={{ position: "absolute", left: `${Math.random() * 100}%`, top: `-10px`, width: `${6 + Math.random() * 6}px`, height: `${6 + Math.random() * 6}px`, backgroundColor: colors[Math.floor(Math.random() * colors.length)], borderRadius: Math.random() > 0.5 ? "50%" : "0", animation: `confettiFall ${2 + Math.random() * 3}s ease-in forwards`, animationDelay: `${Math.random() * 0.5}s` }} />
      ))}
      <style>{`@keyframes confettiFall { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`}</style>
    </div>
  );
}

// 3D Tilt stat card
function StatCard({ label, value, icon: Icon, color, dark, delay = 0 }) {
  const animated = useCountUp(value);
  const cb = dark ? "bg-[#222] border-[#333]" : "bg-white border-wedding-border";
  const ts = dark ? "text-gray-400" : "text-wedding-text-secondary";
  const ref = useRef(null);
  const handleMouse = (e) => {
    const c = ref.current; if (!c) return;
    const r = c.getBoundingClientRect();
    const rx = ((e.clientY - r.top - r.height / 2) / (r.height / 2)) * -8;
    const ry = ((e.clientX - r.left - r.width / 2) / (r.width / 2)) * 8;
    c.style.transform = `perspective(600px) rotateX(${rx}deg) rotateY(${ry}deg) scale(1.03)`;
  };
  const handleLeave = () => { if (ref.current) ref.current.style.transform = "perspective(600px) rotateX(0) rotateY(0) scale(1)"; };
  return (
    <Card ref={ref} onMouseMove={handleMouse} onMouseLeave={handleLeave}
      className={`${cb} transition-transform duration-200 hover:shadow-xl opacity-0 cursor-default`}
      style={{ animation: `staggerIn 0.5s ease forwards ${delay}s`, transformStyle: "preserve-3d" }}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className={`text-sm font-sans font-medium ${ts}`}>{label}</CardTitle>
        <Icon className={`h-4 w-4 ${color}`} />
      </CardHeader>
      <CardContent><div className={`text-3xl font-serif ${color}`}>{animated}</div></CardContent>
    </Card>
  );
}

// 3D Wedding Rings
function WeddingRings({ dark }) {
  return (
    <div className="flex justify-center mb-2">
      <svg width="80" height="50" viewBox="0 0 80 50" className="opacity-60" style={{ animation: "ringsFloat 4s ease-in-out infinite" }}>
        <ellipse cx="28" cy="25" rx="18" ry="18" fill="none" stroke={dark ? "#C48B8B" : "#C48B8B"} strokeWidth="2.5" style={{ animation: "ringRotate1 6s linear infinite" }} />
        <ellipse cx="52" cy="25" rx="18" ry="18" fill="none" stroke={dark ? "#E8C4C4" : "#A06B6B"} strokeWidth="2.5" style={{ animation: "ringRotate2 6s linear infinite" }} />
        <circle cx="40" cy="12" r="2" fill={dark ? "#C48B8B" : "#C48B8B"} style={{ animation: "sparkle 2s ease-in-out infinite" }} />
      </svg>
    </div>
  );
}

// 3D Pie Chart
function MenuPie3D({ menuCounts, dark }) {
  const total = Object.values(menuCounts).reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  const colors = { teletina: "#C48B8B", piletina: "#A06B6B", djeciji: "#E8C4C4" };
  const darkColors = { teletina: "#D4A0A0", piletina: "#B07878", djeciji: "#E0B5B5" };
  let offset = 0;
  const segments = Object.entries(menuCounts).map(([key, count]) => {
    const pct = (count / total) * 100;
    const seg = { key, count, pct, offset, color: dark ? darkColors[key] || "#999" : colors[key] || "#999" };
    offset += pct;
    return seg;
  });

  return (
    <div className="flex items-center gap-6">
      <div className="relative w-28 h-28 flex-shrink-0" style={{ perspective: "400px" }}>
        {/* 3D layers */}
        {[0, 1, 2, 3].map(layer => (
          <svg key={layer} viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" style={{
            transform: `rotateX(55deg) translateZ(${layer * 3}px)`,
            transformStyle: "preserve-3d",
            opacity: layer === 0 ? 0.3 : layer === 3 ? 1 : 0.6,
            animation: `pieReveal 1s ease forwards ${layer * 0.1}s`
          }}>
            {(() => {
              let cum = 0;
              return segments.map(s => {
                const startAngle = (cum / 100) * 360 - 90;
                cum += s.pct;
                const endAngle = (cum / 100) * 360 - 90;
                const largeArc = s.pct > 50 ? 1 : 0;
                const r = 45;
                const x1 = 50 + r * Math.cos((startAngle * Math.PI) / 180);
                const y1 = 50 + r * Math.sin((startAngle * Math.PI) / 180);
                const x2 = 50 + r * Math.cos((endAngle * Math.PI) / 180);
                const y2 = 50 + r * Math.sin((endAngle * Math.PI) / 180);
                return <path key={s.key} d={`M50,50 L${x1},${y1} A${r},${r} 0 ${largeArc},1 ${x2},${y2} Z`} fill={s.color} stroke={dark ? "#222" : "#fff"} strokeWidth="2" />;
              });
            })()}
          </svg>
        ))}
      </div>
      <div className="space-y-2">
        {segments.map(s => (
          <div key={s.key} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
            <p className={`font-sans text-xs ${dark ? "text-gray-300" : "text-wedding-text"}`}>{getMenuLabel(s.key)}</p>
            <p className={`font-sans text-xs font-medium ${dark ? "text-gray-400" : "text-wedding-text-secondary"}`}>{s.count} ({Math.round(s.pct)}%)</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// 3D Progress Cylinder
function ProgressCylinder({ progress, attending, target, dark }) {
  const tp = dark ? "text-white" : "text-wedding-text";
  const ts = dark ? "text-gray-400" : "text-wedding-text-secondary";
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Target className="w-4 h-4 text-wedding-gold" /><p className={`font-sans text-xs uppercase tracking-wider ${ts}`}>Cilj: {target}</p></div>
        <p className={`font-serif text-lg ${tp}`}>{Math.round(progress)}%</p>
      </div>
      <div className="relative h-10" style={{ perspective: "300px" }}>
        <div className={`absolute inset-0 rounded-full overflow-hidden ${dark ? "bg-[#333]" : "bg-wedding-border/30"}`} style={{ transform: "rotateX(20deg)", transformOrigin: "center center" }}>
          <div className="absolute inset-y-0 left-0 rounded-full" style={{
            width: `${progress}%`,
            background: "linear-gradient(180deg, #D4A0A0 0%, #C48B8B 40%, #A06B6B 100%)",
            animation: "progressFill 1.5s ease forwards",
            boxShadow: "0 2px 10px rgba(196,139,139,0.4)"
          }} />
          {/* Shine effect */}
          <div className="absolute inset-0 rounded-full" style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
          }} />
        </div>
      </div>
      <p className={`font-sans text-xs mt-2 ${ts}`}>{attending} od {target} potvrđeno</p>
    </div>
  );
}

// Mini chart with animated bars
function MiniChart({ data, dark }) {
  const entries = Object.entries(data);
  if (entries.length === 0) return <p className={`font-sans text-xs ${dark ? "text-gray-500" : "text-wedding-text-secondary/50"}`}>Nema podataka</p>;
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="flex items-end gap-1 h-20">
      {entries.slice(-14).map(([day, count], i) => (
        <div key={day} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full bg-wedding-gold/80 rounded-t-sm" style={{ height: `${(count / max) * 100}%`, minHeight: count > 0 ? "4px" : "0", animation: `barGrow 0.6s ease forwards ${i * 0.05}s`, transformOrigin: "bottom" }} />
          <p className={`text-[8px] ${dark ? "text-gray-500" : "text-wedding-text-secondary/50"}`}>{day}</p>
        </div>
      ))}
    </div>
  );
}

// Hour heatmap
function HourHeatmap({ data, dark }) {
  const max = Math.max(...data, 1);
  return (
    <div className="grid grid-cols-12 gap-1">
      {data.map((count, hour) => (
        <div key={hour} className="flex flex-col items-center">
          <div className="w-full aspect-square rounded-sm transition-all duration-500" style={{ backgroundColor: count === 0 ? (dark ? "#2a2a2a" : "#f0ece7") : `rgba(197, 160, 89, ${0.2 + (count / max) * 0.8})`, animation: `fadeScale 0.4s ease forwards ${hour * 0.03}s` }} title={`${hour}:00 - ${count}`} />
          {hour % 3 === 0 && <p className={`text-[7px] mt-0.5 ${dark ? "text-gray-600" : "text-wedding-text-secondary/40"}`}>{hour}h</p>}
        </div>
      ))}
    </div>
  );
}

// Pie chart for menus
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return "upravo sada";
  if (mins < 60) return `prije ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `prije ${hours}h`;
  return `prije ${Math.floor(hours / 24)} dana`;
}

function printGuests(guests) {
  const rows = guests.map(g => `<tr><td style="padding:6px 12px;border-bottom:1px solid #eee;">${g.full_name}</td><td style="padding:6px 12px;border-bottom:1px solid #eee;">${g.attending ? "Da" : "Ne"}</td><td style="padding:6px 12px;border-bottom:1px solid #eee;">${getMenuLabel(g.menu_choice)}</td></tr>`).join("");
  const w = window.open("", "", "width=800,height=600");
  w.document.write(`<html><head><title>Lista gostiju</title><style>body{font-family:Georgia,serif;padding:40px;}h1{color:#C5A059;margin-bottom:20px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 12px;border-bottom:2px solid #C5A059;}</style></head><body><h1>Amel & Meliha - Lista gostiju</h1><p>Ukupno: ${guests.length} gostiju</p><table><tr><th>Ime</th><th>Dolazi</th><th>Meni</th></tr>${rows}</table></body></html>`);
  w.document.close();
  w.print();
}

function exportCSV(guests) {
  const header = "Ime,Status,Meni,Napomena,Grupa,Datum";
  const rows = guests.map(g => `"${g.full_name}","${g.attending ? "Dolazi" : "Ne dolazi"}","${getMenuLabel(g.menu_choice)}","${(g.notes || "").replace(/"/g, '""')}","${g.group_id?.slice(0, 8) || ""}","${new Date(g.created_at).toLocaleDateString("bs-BA")}"`);
  const blob = new Blob(["\uFEFF" + [header, ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "gosti_vjencanje.csv"; a.click();
}

// Notification sound
const playNotifSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.setValueAtTime(1200, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
  } catch {}
};

// Nježna romantična melodija za intro (C major arpeggio → soft bell chord)
const playRomanticChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") { ctx.resume().catch(() => {}); }
    const now = ctx.currentTime;
    // Tri nježna tona u nizu (C5, E5, G5) + held major chord
    const notes = [
      { freq: 523.25, start: 0.0, dur: 1.2 }, // C5
      { freq: 659.25, start: 0.35, dur: 1.2 }, // E5
      { freq: 783.99, start: 0.7, dur: 1.6 }, // G5
      { freq: 1046.5, start: 1.0, dur: 2.2 }, // C6
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);
      osc.connect(gain); gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.15, now + start + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      osc.start(now + start);
      osc.stop(now + start + dur);
    });
  } catch {}
};

function AdminLogin({ onLogin, dark }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try { const res = await axios.post(`${API}/admin/login`, { password }); if (res.data.authenticated) { sessionStorage.setItem("admin_token", res.data.token); onLogin(res.data.token); } }
    catch { setError("Pogrešna lozinka"); } finally { setLoading(false); }
  };
  return (
    <div className={`min-h-screen flex items-center justify-center px-4 admin-rose ${dark ? "bg-[#1a1a1a]" : "bg-wedding-bg"}`}>
      <div className={`border p-8 md:p-12 w-full max-w-sm text-center ${dark ? "bg-[#222] border-[#333]" : "bg-white border-wedding-gold/20"}`} data-testid="admin-login">
        <div className={`w-14 h-14 mx-auto mb-6 rounded-full border-2 flex items-center justify-center ${dark ? "border-wedding-gold/30" : "border-wedding-gold/40"}`}><Lock className="w-6 h-6 text-wedding-gold" /></div>
        <h2 className={`font-serif text-2xl mb-2 ${dark ? "text-white" : "text-wedding-text"}`}>Admin Panel</h2>
        <p className={`font-sans text-sm mb-8 ${dark ? "text-gray-400" : "text-wedding-text-secondary"}`}>Unesite lozinku za pristup</p>
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Lozinka" data-testid="admin-password-input" className={`w-full bg-transparent border-0 border-b rounded-none px-0 focus-visible:ring-0 text-center ${dark ? "border-[#444] focus-visible:border-wedding-gold text-white" : "border-wedding-gold/30 focus-visible:border-wedding-gold"}`} />
          {error && <p className="text-wedding-error text-sm font-sans">{error}</p>}
          <Button type="submit" disabled={loading || !password} data-testid="admin-login-button" className="w-full bg-wedding-gold text-white hover:bg-wedding-gold-dark font-serif text-base tracking-wide rounded-sm py-5">{loading ? "..." : "Prijava"}</Button>
        </form>
        <a href="/" className="inline-block mt-6 font-sans text-xs text-wedding-text-secondary/50 hover:text-wedding-gold transition-colors">← Nazad</a>
      </div>
    </div>
  );
}

function GuestFormDialog({ open, onClose, onSave, guest, token, dark }) {
  const [formData, setFormData] = useState({ full_name: "", attending: true, menu_choice: "", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (guest) setFormData({ full_name: guest.full_name || "", attending: guest.attending ?? true, menu_choice: guest.menu_choice || "", notes: guest.notes || "" }); else setFormData({ full_name: "", attending: true, menu_choice: "", notes: "" }); }, [guest, open]);
  const handleSave = async () => {
    if (!formData.full_name.trim()) { toast.error("Ime je obavezno"); return; }
    setSaving(true);
    try { const headers = { Authorization: `Bearer ${token}` }; if (guest) { await axios.put(`${API}/guests/${guest.id}`, formData, { headers }); toast.success("Ažurirano"); } else { await axios.post(`${API}/guests/admin`, formData, { headers }); toast.success("Dodano"); } onSave(); onClose(); }
    catch { toast.error("Greška"); } finally { setSaving(false); }
  };
  const ib = dark ? "bg-[#1a1a1a] border-[#444] text-white focus-visible:border-wedding-gold" : "border-wedding-border focus-visible:border-wedding-text";
  const lb = dark ? "text-gray-400" : "text-wedding-text-secondary";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`sm:max-w-md ${dark ? "bg-[#222] border-[#333]" : "bg-white"}`}>
        <DialogHeader><DialogTitle className={`font-serif text-xl ${dark ? "text-white" : "text-wedding-text"}`}>{guest ? "Uredi" : "Dodaj"}</DialogTitle></DialogHeader>
        <div className="space-y-5 py-4">
          <div><label className={`font-sans text-xs uppercase tracking-[0.15em] block mb-2 ${lb}`}>Ime *</label><Input value={formData.full_name} onChange={(e) => setFormData(p => ({ ...p, full_name: e.target.value }))} className={`w-full focus-visible:ring-0 ${ib}`} placeholder="Ime i prezime" /></div>
          <div><label className={`font-sans text-xs uppercase tracking-[0.15em] block mb-2 ${lb}`}>Status</label><Select value={formData.attending ? "true" : "false"} onValueChange={(v) => setFormData(p => ({ ...p, attending: v === "true" }))}><SelectTrigger className={`w-full focus:ring-0 ${ib}`}><SelectValue /></SelectTrigger><SelectContent className={dark ? "bg-[#222] border-[#444]" : "bg-white"}><SelectItem value="true">Dolazi</SelectItem><SelectItem value="false">Ne dolazi</SelectItem></SelectContent></Select></div>
          {formData.attending && <div><label className={`font-sans text-xs uppercase tracking-[0.15em] block mb-2 ${lb}`}>Meni</label><Select value={formData.menu_choice || ""} onValueChange={(v) => setFormData(p => ({ ...p, menu_choice: v }))}><SelectTrigger className={`w-full focus:ring-0 ${ib}`}><SelectValue placeholder="Odaberite" /></SelectTrigger><SelectContent className={dark ? "bg-[#222] border-[#444]" : "bg-white"}>{MENU_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>}
          <div><label className={`font-sans text-xs uppercase tracking-[0.15em] block mb-2 ${lb}`}>Napomena</label><Textarea value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} className={`w-full focus-visible:ring-0 resize-none min-h-[60px] ${ib}`} placeholder="Alergije ili zahtjevi" /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className={dark ? "border-[#444] text-gray-300" : "border-wedding-border"}>Odustani</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-wedding-gold text-white hover:bg-wedding-gold-dark">{saving ? "..." : "Spremi"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(null);
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState({ total_submissions: 0, total_guests: 0, attending: 0, not_attending: 0 });
  const [visitorStats, setVisitorStats] = useState({ active_now: 0, total_visitors: 0, countries: {} });
  const [adminLogs, setAdminLogs] = useState([]);
  const [activity, setActivity] = useState({ daily: {}, hourly: Array(24).fill(0), last_signup: null });
  const [guestCountries, setGuestCountries] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [dark, setDark] = useState(() => localStorage.getItem("admin_dark") === "true");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMenu, setFilterMenu] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [logsOpen, setLogsOpen] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [heartbeatGuest, setHeartbeatGuest] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("admin_sound") !== "false");
  const prevGuestCount = useRef(0);

  useEffect(() => { localStorage.setItem("admin_dark", dark); }, [dark]);
  useEffect(() => { localStorage.setItem("admin_sound", soundOn); }, [soundOn]);

  // Play romantic chime when intro first renders (user has just clicked "Prijava", so autoplay is allowed)
  useEffect(() => {
    if (showIntro && soundOn) {
      const t = setTimeout(() => playRomanticChime(), 300);
      return () => clearTimeout(t);
    }
  }, [showIntro, soundOn]);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  useEffect(() => { const saved = sessionStorage.getItem("admin_token"); if (saved) setToken(saved); }, []);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [gRes, sRes, vRes, lRes, aRes, cRes] = await Promise.all([
        axios.get(`${API}/guests`, authHeaders()), axios.get(`${API}/guests/stats`, authHeaders()),
        axios.get(`${API}/visitors/stats`, authHeaders()), axios.get(`${API}/admin/logs`, authHeaders()),
        axios.get(`${API}/guests/activity`, authHeaders()), axios.get(`${API}/guests/countries`, authHeaders()),
      ]);
      if (prevGuestCount.current > 0 && gRes.data.length > prevGuestCount.current) {
        setShowConfetti(true); setTimeout(() => setShowConfetti(false), 4000);
        if (soundOn) playNotifSound();
        // Heartbeat for new guest
        const newest = gRes.data[0];
        if (newest) { setHeartbeatGuest(newest.full_name); setTimeout(() => setHeartbeatGuest(null), 5000); }
        // Fireworks at milestones
        const newTotal = sRes.data.attending;
        const oldTotal = stats.attending;
        const milestones = [50, 75, 100, 130];
        for (const m of milestones) {
          if (oldTotal < m && newTotal >= m) {
            setShowFireworks(true); setTimeout(() => setShowFireworks(false), 5000);
            break;
          }
        }
      }
      prevGuestCount.current = gRes.data.length;
      setGuests(gRes.data); setStats(sRes.data); setVisitorStats(vRes.data); setAdminLogs(lRes.data); setActivity(aRes.data); setGuestCountries(cRes.data);
      const lastSeen = localStorage.getItem("admin_last_seen");
      if (lastSeen) setNewCount(gRes.data.filter(g => new Date(g.created_at) > new Date(lastSeen)).length);
      localStorage.setItem("admin_last_seen", new Date().toISOString());
    } catch (error) {
      if (error.response?.status === 401) { sessionStorage.removeItem("admin_token"); setToken(null); toast.error("Sesija istekla"); return; }
      toast.error("Greška");
    } finally { setLoading(false); setRefreshing(false); }
  }, [token, authHeaders, soundOn]);

  useEffect(() => { if (token) { setLoading(true); fetchData(); } }, [token, fetchData]);
  useEffect(() => { if (!token) return; const i = setInterval(fetchData, 30000); return () => clearInterval(i); }, [token, fetchData]);

  const handleDelete = async (id) => { try { await axios.delete(`${API}/guests/${id}`, authHeaders()); toast.success("Obrisano"); fetchData(); } catch { toast.error("Greška"); } };
  const handleLogout = () => { sessionStorage.removeItem("admin_token"); setToken(null); };
  const formatDate = (d) => new Date(d).toLocaleDateString("bs-BA", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const filteredGuests = useMemo(() => {
    let r = guests;
    if (searchQuery) { const q = searchQuery.toLowerCase(); r = r.filter(g => g.full_name.toLowerCase().includes(q)); }
    if (filterStatus === "attending") r = r.filter(g => g.attending);
    if (filterStatus === "not_attending") r = r.filter(g => !g.attending);
    if (filterMenu !== "all") r = r.filter(g => g.menu_choice === filterMenu);
    return r;
  }, [guests, searchQuery, filterStatus, filterMenu]);

  const groupedGuests = useMemo(() => {
    const groups = {};
    filteredGuests.forEach(g => { const gid = g.group_id || g.id; if (!groups[gid]) groups[gid] = []; groups[gid].push(g); });
    return Object.values(groups);
  }, [filteredGuests]);

  const visibleGroups = groupedGuests.slice(0, visibleCount);
  const hasMore = visibleCount < groupedGuests.length;
  const menuCounts = {};
  guests.filter(g => g.attending && g.menu_choice).forEach(g => { menuCounts[g.menu_choice] = (menuCounts[g.menu_choice] || 0) + 1; });
  const progress = Math.min((stats.attending / GUEST_TARGET) * 100, 100);

  const bg = dark ? "bg-[#1a1a1a]" : "bg-wedding-bg";
  const cb = dark ? "bg-[#222] border-[#333]" : "bg-white border-wedding-border";
  const tp = dark ? "text-white" : "text-wedding-text";
  const ts = dark ? "text-gray-400" : "text-wedding-text-secondary";
  const ib = dark ? "bg-[#1a1a1a] border-[#444] text-white placeholder:text-gray-500" : "border-wedding-border";
  const btnOutline = dark ? "border-[#444] text-gray-300 hover:bg-[#333]" : "border-wedding-border text-wedding-text-secondary";

  if (!token) return <AdminLogin onLogin={(t) => setToken(t)} dark={dark} />;
  
  // Cinematic Intro
  if (showIntro) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center overflow-hidden" onClick={() => { setShowIntro(false); }}>
        <style>{`
          @keyframes introParticle { 0% { opacity:0; transform:scale(0); } 50% { opacity:1; } 100% { opacity:0; transform:scale(1) translateY(-100px); } }
          @keyframes introText { 0% { opacity:0; letter-spacing:0.5em; filter:blur(8px); } 100% { opacity:1; letter-spacing:0.2em; filter:blur(0); } }
          @keyframes introSub { 0% { opacity:0; transform:translateY(20px); } 100% { opacity:1; transform:translateY(0); } }
          @keyframes introLine { 0% { width:0; } 100% { width:120px; } }
          @keyframes heartBeat { 0%,100% { transform:scale(1); } 15% { transform:scale(1.3); } 30% { transform:scale(1); } 45% { transform:scale(1.2); } 60% { transform:scale(1); } }
          @keyframes heartGlow { 0%,100% { filter:drop-shadow(0 0 8px rgba(196,139,139,0.3)); } 50% { filter:drop-shadow(0 0 25px rgba(196,139,139,0.8)); } }
          @keyframes volimTeReveal { 0% { opacity:0; transform:scale(0.5); letter-spacing:0.5em; } 50% { opacity:1; transform:scale(1.1); } 100% { opacity:1; transform:scale(1); letter-spacing:0.15em; } }
        `}</style>
        {Array.from({ length: 40 }, (_, i) => (
          <div key={i} className="absolute rounded-full" style={{
            width: `${2 + Math.random() * 4}px`, height: `${2 + Math.random() * 4}px`,
            background: `rgba(196, 139, 139, ${0.3 + Math.random() * 0.7})`,
            left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
            animation: `introParticle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 2}s`
          }} />
        ))}
        <div className="text-center z-10 px-6">
          <div className="h-px bg-gradient-to-r from-transparent via-[#C48B8B] to-transparent mx-auto mb-8" style={{ animation: "introLine 1.5s ease forwards" }} />
          
          <p className="font-serif text-sm md:text-base text-[#C48B8B]/70 italic mb-6 opacity-0" style={{ animation: "introSub 1s ease forwards 0.5s" }}>
            Jer kad je Allah spojio naša srca, spojio je i naše sudbine
          </p>

          {/* Heart with animation */}
          <div className="my-8 opacity-0" style={{ animation: "introSub 0.8s ease forwards 1.2s" }}>
            <svg viewBox="0 0 100 90" className="w-20 h-20 mx-auto" style={{ animation: "heartBeat 1.5s ease-in-out infinite 2s, heartGlow 2s ease-in-out infinite 2s" }}>
              <path d="M50 88 C25 65 0 45 0 25 C0 10 12 0 25 0 C35 0 45 8 50 18 C55 8 65 0 75 0 C88 0 100 10 100 25 C100 45 75 65 50 88Z" fill="#C48B8B" />
            </svg>
          </div>

          {/* VOLIM TE */}
          <h1 className="font-serif text-5xl md:text-7xl text-white uppercase opacity-0" style={{ animation: "volimTeReveal 1.5s ease forwards 2s" }}>
            Volim te
          </h1>

          <div className="h-px bg-gradient-to-r from-transparent via-[#C48B8B] to-transparent mx-auto mt-8" style={{ animation: "introLine 1.5s ease forwards 0.5s" }} />
          
          <p className="font-serif text-xl md:text-2xl text-white/60 tracking-[0.15em] uppercase mt-8 opacity-0" style={{ animation: "introSub 1s ease forwards 3s" }}>
            Amel & Meliha
          </p>

          <p className="font-sans text-[10px] text-white/20 mt-10 opacity-0" style={{ animation: "introSub 1s ease forwards 4s" }}>
            Klikni za nastavak
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <div className={`min-h-screen ${bg} flex items-center justify-center`}><div className="text-wedding-gold font-serif text-xl">Učitavanje...</div></div>;

  // AI Summary
  const aiSummary = (() => {
    const topMenu = Object.entries(menuCounts).sort((a, b) => b[1] - a[1])[0];
    const lastTime = activity.last_signup ? timeAgo(activity.last_signup.time) : "nema";
    const pct = stats.total_guests > 0 ? Math.round((stats.attending / GUEST_TARGET) * 100) : 0;
    return `${stats.total_guests} gostiju ukupno, ${stats.attending} dolazi (${pct}% cilja). ${topMenu ? `${getMenuLabel(topMenu[0])} je najčešći izbor (${topMenu[1]}x).` : ""} Zadnja prijava: ${lastTime}.`;
  })();

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300 admin-rose`} data-testid="admin-page">
      <Confetti active={showConfetti} />
      {/* Fireworks */}
      {showFireworks && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {Array.from({ length: 30 }, (_, i) => {
            const cx = 30 + Math.random() * 40;
            const cy = 20 + Math.random() * 30;
            return Array.from({ length: 12 }, (_, j) => {
              const angle = (j / 12) * 360;
              const dist = 80 + Math.random() * 120;
              const dx = Math.cos(angle * Math.PI / 180) * dist;
              const dy = Math.sin(angle * Math.PI / 180) * dist;
              return <div key={`${i}-${j}`} className="absolute rounded-full" style={{
                width: "4px", height: "4px",
                background: ["#C48B8B", "#E8C4C4", "#A06B6B", "#FFD700", "#fff"][Math.floor(Math.random() * 5)],
                left: `${cx}%`, top: `${cy}%`,
                animation: `firework 1.5s ease-out forwards`,
                animationDelay: `${i * 0.1}s`,
                "--dx": `${dx}px`, "--dy": `${dy}px`,
              }} />;
            });
          }).flat()}
          <style>{`@keyframes firework { 0% { transform:translate(0,0) scale(1); opacity:1; } 100% { transform:translate(var(--dx),var(--dy)) scale(0); opacity:0; } }`}</style>
        </div>
      )}
      {/* Heartbeat notification */}
      {heartbeatGuest && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-full bg-white shadow-xl border border-[#E8C4C4] flex items-center gap-3" style={{ animation: "staggerIn 0.3s ease forwards" }}>
          <div className="relative">
            <div className="w-3 h-3 rounded-full bg-[#C48B8B]" />
            <div className="absolute inset-0 w-3 h-3 rounded-full bg-[#C48B8B] animate-ping" />
          </div>
          <p className="font-sans text-sm text-wedding-text"><span className="font-medium">{heartbeatGuest}</span> se upravo prijavio/la</p>
        </div>
      )}
      {/* Particle background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className="absolute rounded-full opacity-20" style={{
            width: `${2 + Math.random() * 3}px`, height: `${2 + Math.random() * 3}px`,
            background: dark ? "#C48B8B" : "#C48B8B",
            left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
            animation: `particleFloat ${8 + Math.random() * 12}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
          }} />
        ))}
      </div>
      <style>{`
        @keyframes staggerIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes barGrow { from { transform:scaleY(0); } to { transform:scaleY(1); } }
        @keyframes fadeScale { from { opacity:0; transform:scale(0.8); } to { opacity:1; transform:scale(1); } }
        @keyframes pieReveal { from { transform:rotate(-90deg) scale(0.5); opacity:0; } to { transform:rotate(0) scale(1); opacity:1; } }
        @keyframes progressFill { from { width:0; } }
        @keyframes particleFloat { 0%,100% { transform:translateY(0) translateX(0); } 25% { transform:translateY(-30px) translateX(10px); } 50% { transform:translateY(-10px) translateX(-15px); } 75% { transform:translateY(-40px) translateX(5px); } }
        @keyframes flipIn { 0% { transform:rotateX(90deg); opacity:0; } 100% { transform:rotateX(0); opacity:1; } }
        @keyframes timelineSlide { from { opacity:0; transform:translateX(-20px); } to { opacity:1; transform:translateX(0); } }
        @keyframes ringsFloat { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-5px); } }
        @keyframes ringRotate1 { 0% { transform:rotateY(0deg); } 100% { transform:rotateY(360deg); } }
        @keyframes ringRotate2 { 0% { transform:rotateY(180deg); } 100% { transform:rotateY(540deg); } }
        @keyframes sparkle { 0%,100% { opacity:0.3; r:1.5; } 50% { opacity:1; r:3; } }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative z-10">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-5">
          <a href="/" className={`inline-flex items-center gap-1.5 font-sans text-sm ${ts} hover:text-wedding-gold transition-colors`} data-testid="home-link"><Home className="w-4 h-4" /> Pozivnica</a>
          <div className="flex gap-1.5">
            <Button onClick={() => setDark(!dark)} variant="outline" size="sm" className={`h-8 w-8 p-0 ${btnOutline}`}>{dark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}</Button>
            <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className={`h-8 w-8 p-0 ${btnOutline}`}>{soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}</Button>
            <Button onClick={() => setQrOpen(true)} variant="outline" size="sm" className={`h-8 w-8 p-0 ${btnOutline}`}><QrCode className="w-3.5 h-3.5" /></Button>
            <Button onClick={() => setPwOpen(true)} variant="outline" size="sm" className={`h-8 w-8 p-0 ${btnOutline}`}><KeyRound className="w-3.5 h-3.5" /></Button>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
          <div>
            <div className="flex items-center gap-3">
              <h1 className={`font-serif text-3xl md:text-4xl ${tp}`}>Pregled Prijava</h1>
              {newCount > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-wedding-gold text-white text-xs font-sans animate-pulse"><Bell className="w-3 h-3" /> {newCount}</span>}
            </div>
            <p className={`font-sans text-sm mt-1 ${ts}`}>Amel & Meliha · Šerijatsko vjenčanje</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => { setEditingGuest(null); setDialogOpen(true); }} size="sm" className="bg-wedding-gold text-white hover:bg-wedding-gold-dark"><Plus className="w-4 h-4 mr-1" /> Dodaj</Button>
            <Button onClick={() => exportCSV(guests)} variant="outline" size="sm" className={dark ? "border-[#444] text-gray-300 hover:bg-[#333]" : "border-wedding-gold text-wedding-gold hover:bg-wedding-gold hover:text-white"}><Download className="w-4 h-4 mr-1" /> CSV</Button>
            <Button onClick={() => printGuests(guests)} variant="outline" size="sm" className={dark ? "border-[#444] text-gray-300 hover:bg-[#333]" : "border-wedding-gold text-wedding-gold hover:bg-wedding-gold hover:text-white"}><Printer className="w-4 h-4 mr-1" /> Print</Button>
            <Button onClick={() => { setRefreshing(true); fetchData(); }} disabled={refreshing} variant="outline" size="sm" className={dark ? "border-[#444] text-gray-300 hover:bg-[#333]" : "border-wedding-gold text-wedding-gold hover:bg-wedding-gold hover:text-white"}><RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /></Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className={dark ? "border-[#444] text-gray-300 hover:bg-red-900/30 hover:text-red-400" : "border-wedding-border text-wedding-text-secondary hover:bg-red-50 hover:text-wedding-error"}>Odjava</Button>
          </div>
        </div>

        {/* Admin Logs - at top in accordion */}
        {adminLogs.length > 0 && (
          <Card className={`${cb} mb-5`}>
            <CardHeader className="cursor-pointer py-3" onClick={() => setLogsOpen(!logsOpen)}>
              <div className="flex items-center justify-between"><CardTitle className={`font-sans text-sm font-medium ${ts}`}>Admin prijave ({adminLogs.length})</CardTitle>{logsOpen ? <ChevronUp className={`w-4 h-4 ${ts}`} /> : <ChevronDown className={`w-4 h-4 ${ts}`} />}</div>
            </CardHeader>
            {logsOpen && <CardContent className="pt-0"><div className="space-y-2 max-h-60 overflow-y-auto">{adminLogs.map((log, i) => (
              <div key={i} className={`flex items-center justify-between py-2 border-b last:border-0 ${dark ? "border-[#333]" : "border-wedding-border/30"}`}>
                <div className="flex items-center gap-2"><div className={`w-2 h-2 rounded-full ${log.success ? "bg-green-500" : "bg-red-500"}`} /><div><p className={`font-sans text-sm ${tp}`}>{log.city ? `${log.city}, ` : ""}{log.country}</p><p className={`font-sans text-[10px] ${ts} opacity-50`}>{log.ip}</p></div></div>
                <div className="text-right"><p className={`font-sans text-xs ${log.success ? "text-green-500" : "text-red-500"}`}>{log.success ? "OK" : "Fail"}</p><p className={`font-sans text-[10px] ${ts} opacity-50`}>{new Date(log.timestamp).toLocaleDateString("bs-BA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p></div>
              </div>
            ))}</div></CardContent>}
          </Card>
        )}

        {/* Countdown + Progress */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <Card className={`${cb} opacity-0`} style={{ animation: "staggerIn 0.5s ease forwards 0.05s" }}>
            <CardContent className="py-4">
              <div className="flex items-center justify-center gap-2 mb-3"><Calendar className="w-4 h-4 text-wedding-gold" /><p className={`font-sans text-xs uppercase tracking-wider ${ts}`}>Do vjenčanja</p></div>
              <Countdown dark={dark} />
            </CardContent>
          </Card>
          <Card className={`${cb} opacity-0`} style={{ animation: "staggerIn 0.5s ease forwards 0.1s" }}>
            <CardContent className="py-4">
              <ProgressCylinder progress={progress} attending={stats.attending} target={GUEST_TARGET} dark={dark} />
            </CardContent>
          </Card>
        </div>

        {/* Last activity + Live */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <Card className={`${cb} opacity-0`} style={{ animation: "staggerIn 0.5s ease forwards 0.15s" }}>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-2"><PartyPopper className="w-4 h-4 text-wedding-gold" /><p className={`font-sans text-xs uppercase tracking-wider ${ts}`}>Zadnja prijava</p></div>
              {(() => {
                if (guests.length === 0) return <p className={`font-sans text-sm ${ts}`}>Nema</p>;
                const lastGroupId = guests[0]?.group_id;
                const lastGroup = lastGroupId ? guests.filter(g => g.group_id === lastGroupId) : [guests[0]];
                return (
                  <div>
                    {lastGroup.map((g, i) => (
                      <div key={g.id} className={`${i > 0 ? "mt-1.5" : ""}`}>
                        <p className={`font-sans font-medium text-sm ${tp}`}>{g.full_name}{g.menu_choice ? <span className={`font-normal text-xs ${ts}`}> · {getMenuLabel(g.menu_choice)}</span> : ""}</p>
                      </div>
                    ))}
                    <p className={`font-sans text-xs mt-2 ${ts}`}>{timeAgo(lastGroup[0]?.created_at)} · {lastGroup[0]?.attending ? "Dolaze" : "Ne dolaze"} ({lastGroup.length} {lastGroup.length === 1 ? "osoba" : "osobe"})</p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
          <Card className={`${cb} opacity-0`} style={{ animation: "staggerIn 0.5s ease forwards 0.2s" }}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2"><Eye className="w-4 h-4 text-green-500" /><p className={`font-sans text-xs uppercase tracking-wider ${ts}`}>Aktivno sada</p></div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /><span className="font-serif text-lg text-green-500">{visitorStats.active_now}</span></div>
              </div>
              <p className={`font-sans text-xs ${ts}`}>Ukupno: {visitorStats.total_visitors}</p>
              {Object.keys(visitorStats.countries).length > 0 && <div className="mt-2 flex flex-wrap gap-1">{Object.entries(visitorStats.countries).sort((a, b) => b[1] - a[1]).map(([c, n]) => <span key={c} className={`font-sans text-[10px] px-1.5 py-0.5 rounded ${dark ? "bg-[#333] text-gray-300" : "bg-wedding-bg text-wedding-text-secondary"}`}>{c} ({n})</span>)}</div>}
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <StatCard label="Gostiju" value={stats.total_guests} icon={Users} color="text-wedding-gold" dark={dark} delay={0.25} />
          <StatCard label="Dolaze" value={stats.attending} icon={UserCheck} color="text-green-500" dark={dark} delay={0.3} />
          <StatCard label="Ne dolaze" value={stats.not_attending} icon={UserX} color="text-wedding-error" dark={dark} delay={0.35} />
          <StatCard label="Prijava" value={stats.total_submissions} icon={ClipboardList} color={dark ? "text-white" : "text-wedding-text"} dark={dark} delay={0.4} />
        </div>

        {/* Menu Stats + Pie */}
        {Object.keys(menuCounts).length > 0 && (
          <Card className={`${cb} mb-5 transition-all hover:shadow-lg opacity-0`} style={{ animation: "staggerIn 0.5s ease forwards 0.55s" }}>
            <CardHeader><CardTitle className={`font-serif text-xl ${tp}`}><PieChart className="w-5 h-5 inline mr-2 text-wedding-gold" />Pregled menija</CardTitle></CardHeader>
            <CardContent>
              <MenuPie3D menuCounts={menuCounts} dark={dark} />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                {Object.entries(menuCounts).map(([key, count]) => (
                  <div key={key} className={`border rounded-sm p-3 text-center ${dark ? "border-[#333]" : "border-wedding-border"}`}>
                    <p className={`font-sans text-xs uppercase tracking-wider mb-1 ${ts}`}>{getMenuLabel(key)}</p>
                    <p className="font-serif text-2xl text-wedding-gold">{count}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1"><Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${ts} opacity-50`} /><Input value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setVisibleCount(PAGE_SIZE); }} placeholder="Pretraži goste..." className={`pl-10 ${ib} focus-visible:ring-0 focus-visible:border-wedding-gold`} /></div>
          <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setVisibleCount(PAGE_SIZE); }}><SelectTrigger className={`w-full sm:w-[140px] ${ib} focus:ring-0`}><SelectValue /></SelectTrigger><SelectContent className={dark ? "bg-[#222] border-[#444]" : "bg-white"}><SelectItem value="all">Svi</SelectItem><SelectItem value="attending">Dolaze</SelectItem><SelectItem value="not_attending">Ne dolaze</SelectItem></SelectContent></Select>
          <Select value={filterMenu} onValueChange={(v) => { setFilterMenu(v); setVisibleCount(PAGE_SIZE); }}><SelectTrigger className={`w-full sm:w-[140px] ${ib} focus:ring-0`}><SelectValue /></SelectTrigger><SelectContent className={dark ? "bg-[#222] border-[#444]" : "bg-white"}><SelectItem value="all">Svi meniji</SelectItem>{MENU_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
        </div>

        {/* Guest List */}
        <Card className={cb}>
          <CardHeader><CardTitle className={`font-serif text-xl ${tp}`}>Gosti ({filteredGuests.length})</CardTitle></CardHeader>
          <CardContent>
            {filteredGuests.length === 0 ? (
              <div className="text-center py-8"><p className={`font-sans ${ts}`}>{searchQuery || filterStatus !== "all" || filterMenu !== "all" ? "Nema rezultata" : "Još nema gostiju"}</p></div>
            ) : (
              <div className="space-y-3">
                {visibleGroups.map((group) => (
                  <div key={group[0].group_id || group[0].id} className={`rounded-sm overflow-hidden ${group.length > 1 ? `border ${dark ? "border-[#333]" : "border-wedding-gold/20"}` : ""}`}>
                    {group.length > 1 && <div className={`px-4 py-1.5 ${dark ? "bg-[#2a2a2a]" : "bg-wedding-gold/5"}`}><p className={`font-sans text-[10px] uppercase tracking-wider ${dark ? "text-wedding-gold/60" : "text-wedding-gold/80"}`}>Grupa · {group.length} osoba</p></div>}
                    {group.map((g) => (
                      <div key={g.id} className={`p-4 transition-all hover:bg-opacity-50 ${group.length > 1 ? "" : `border ${dark ? "border-[#333] hover:border-[#444]" : "border-wedding-border hover:border-wedding-gold/30"} rounded-sm`} ${dark ? "hover:bg-[#2a2a2a]" : "hover:bg-wedding-gold/5"}`} data-testid={`guest-row-${g.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className={`font-sans font-medium ${tp}`}>{g.full_name}</p>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-sans ${g.attending ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{g.attending ? "Dolazi" : "Ne dolazi"}</span>
                            </div>
                            {g.menu_choice && <p className={`font-sans text-xs ${ts}`}>Meni: {getMenuLabel(g.menu_choice)}</p>}
                            {g.country && <p className={`font-sans text-xs ${ts}`}>{g.city ? `${g.city}, ` : ""}{g.country}</p>}
                            {g.notes && <p className={`font-sans text-xs mt-0.5 ${ts} opacity-70`}>{g.notes}</p>}
                            <p className={`font-sans text-[10px] mt-1 ${ts} opacity-50`}>{formatDate(g.created_at)}</p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button variant="outline" size="sm" onClick={() => { setEditingGuest(g); setDialogOpen(true); }} className={`h-8 w-8 p-0 ${dark ? "border-[#444] text-wedding-gold hover:bg-wedding-gold hover:text-white" : "border-wedding-gold/30 text-wedding-gold hover:bg-wedding-gold hover:text-white"}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild><Button variant="outline" size="sm" className={`h-8 w-8 p-0 ${dark ? "border-[#444] text-red-400 hover:bg-red-900/30" : "border-wedding-error/30 text-wedding-error hover:bg-wedding-error hover:text-white"}`}><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                              <AlertDialogContent className={dark ? "bg-[#222] border-[#333]" : "bg-white"}><AlertDialogHeader><AlertDialogTitle className={`font-serif ${tp}`}>Obrisati?</AlertDialogTitle><AlertDialogDescription className={ts}>Obrisati {g.full_name}?</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className={dark ? "border-[#444] text-gray-300" : ""}>Odustani</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(g.id)} className="bg-wedding-error hover:bg-red-700">Obriši</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                {hasMore && <Button onClick={() => setVisibleCount(v => v + PAGE_SIZE)} variant="outline" className={`w-full ${dark ? "border-[#444] text-gray-300 hover:bg-[#333]" : "border-wedding-border"}`}>Učitaj više ({groupedGuests.length - visibleCount})</Button>}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-5 text-center"><a href="/" className="font-sans text-sm text-wedding-gold hover:text-wedding-gold-dark transition-colors">← Nazad</a></div>
      </div>

      <GuestFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={fetchData} guest={editingGuest} token={token} dark={dark} />

      {/* QR Dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className={`sm:max-w-sm text-center ${dark ? "bg-[#222] border-[#333]" : "bg-white"}`}>
          <DialogHeader><DialogTitle className={`font-serif text-xl ${dark ? "text-white" : "text-wedding-text"}`}>QR Kod</DialogTitle></DialogHeader>
          <div className="py-4">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=https://amel-meliha.com&color=1A1A1A&bgcolor=F5F0EB" alt="QR" className="mx-auto rounded" />
            <p className={`font-sans text-sm mt-4 ${dark ? "text-gray-400" : "text-wedding-text-secondary"}`}>amel-meliha.com</p>
            <p className={`font-sans text-xs mt-1 ${dark ? "text-gray-500" : "text-wedding-text-secondary/50"}`}>Za fizičke pozivnice</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <PasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} token={token} dark={dark} />
    </div>
  );
}

function PasswordDialog({ open, onClose, token, dark }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!oldPw || !newPw) { toast.error("Popunite oba polja"); return; }
    if (newPw.length < 4) { toast.error("Nova lozinka mora imati min 4 znaka"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/change-password`, { old_password: oldPw, new_password: newPw }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Lozinka promijenjena");
      setOldPw(""); setNewPw(""); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška");
    } finally { setSaving(false); }
  };
  const ib = dark ? "bg-[#1a1a1a] border-[#444] text-white" : "border-wedding-border";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`sm:max-w-sm ${dark ? "bg-[#222] border-[#333]" : "bg-white"}`}>
        <DialogHeader><DialogTitle className={`font-serif text-xl ${dark ? "text-white" : "text-wedding-text"}`}>Promjena lozinke</DialogTitle></DialogHeader>
        <div className="space-y-4 py-4">
          <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} placeholder="Stara lozinka" className={`w-full focus-visible:ring-0 ${ib}`} />
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nova lozinka" className={`w-full focus-visible:ring-0 ${ib}`} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className={dark ? "border-[#444] text-gray-300" : "border-wedding-border"}>Odustani</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-wedding-gold text-white hover:bg-wedding-gold-dark">{saving ? "..." : "Spremi"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
